import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";
import SchoolInnerPageBanner from "../../components/SchoolInnerPageBanner";
import useScrollAnimation from "../../constants/useScrollAnimation";
import '../../components/AboutSection.css';
import './AboutPage.css';
const baseAPIUrl = process.env.REACT_APP_API_BASE_URL;

const AboutPage = () => {
  const location = useLocation();

  const { school_slug } = useParams();
  const [mv, setMv] = useState(null);

  const [affiliation, setAffiliation] = useState(null);
  const [awards, setAwards] = useState(null);
  const [csr, setCsr] = useState(null);
  const [mentorship, setMentorship] = useState(null);

  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // For scroll top

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const sectionId = location.state?.scrollTo;
    if (!sectionId) return;

    const timer = setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [location.key]);

  useEffect(() => {
    scrollToTop();
  }, []);

  /* ---------------- FETCH ABOUT DATA ---------------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";

    setLoading(true);

    axios
      .get(`${baseAPIUrl}api/about/${finalSchoolSlug}`)
      .then((res) => {
        const data = res.data?.data;
        setAbout(
          data && Object.values(data).some(v => v !== null && v !== "")
            ? data
            : null
        );
        setLoading(false);
      })
      .catch(() => {
        setAbout(null);
        setLoading(false);
      });
  }, [school_slug]);

  /* ---------------- FETCH MISSION VISION DATA ---------------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";

    axios
      .get(`${baseAPIUrl}api/about/mission_vision/${finalSchoolSlug}`)
      .then((res) => {
        if (
          res.data.status === 1 &&
          res.data.data &&
          (
            res.data.data.heading ||
            res.data.data.mission_content ||
            res.data.data.vission_content
          )
        ) {
          setMv(res.data.data);
        } else {
          setMv(null); // ❌ hide section
        }
      })
      .catch(() => setMv(null));
  }, [school_slug]);

  /* ---------------- FETCH AFFILIATIONS DATA ---------------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";

    axios
      .get(`${baseAPIUrl}api/about/affiliations/${finalSchoolSlug}`)
      .then((res) => {
        if (
          res.data.status === 1 &&
          res.data.data &&
          (
            res.data.data.section?.heading ||
            res.data.data.section?.paragraph_1 ||
            res.data.data.rows?.length > 0
          )
        ) {
          setAffiliation(res.data.data);
        } else {
          setAffiliation(null); // ❌ hide section
        }
      })
      .catch(() => setAffiliation(null));
  }, [school_slug]);

  /* ---------------- FETCH ACCOLADES & AWARDS DATA ---------------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";
    axios
      .get(`${baseAPIUrl}api/about/awards/${finalSchoolSlug}`)
      .then((res) => {
        if (
          res.data.status === 1 &&
          res.data.data &&
          (
            res.data.data.section?.heading ||
            res.data.data.section?.intro_text ||
            res.data.data.items?.length > 0
          )
        ) {
          setAwards(res.data.data);
        } else {
          setAwards(null); // ❌ hide section
        }
      })
      .catch(() => setAwards(null));
  }, [school_slug]);

  /* ---------------- FETCH CSR DATA ---------------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";

    axios
      .get(`${baseAPIUrl}api/about/csr/${finalSchoolSlug}`)
      .then((res) => {
        if (res.data.status === 1) {
          setCsr(res.data.data);
        } else {
          setCsr(null);
        }
      })
      .catch((err) => {
        console.error("CSR API ERROR:", err);
        setCsr(null);
      });
  }, [school_slug]);

  /* ---------------- FETCH MENTORSHIP DATA ---------------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";

    axios
      .get(`${baseAPIUrl}api/about/mentorship/${finalSchoolSlug}`)
      .then((res) => {
        if (res.data.status === 1) {
          setMentorship(res.data.data);
        } else {
          setMentorship(null);
        }
      })
      .catch((err) => {
        console.error("MENTORSHIP API ERROR:", err);
        setMentorship(null);
      });
  }, [school_slug]);




  // For About

  const hasAboutContent =
    about &&
    Object.values(about).some(
      (value) => value !== null && value !== ""
    );

  // For Mission & Vision

  const hasMv =
    !!mv &&
    (mv.heading || mv.mission_content || mv.vission_content || mv.mv_image);
  const hasAffiliation =
    !!affiliation &&
    affiliation.section &&
    (
      affiliation.section.heading ||
      affiliation.section.paragraph_1 ||
      affiliation.rows?.length > 0
    );

  // For Accolades & awards

  const hasAwards =
    !!awards &&
    awards.section &&
    (
      awards.section.heading ||
      awards.items?.length > 0 ||
      awards.section.highlight_text
    );

  // For CSR

  const hasCsr =
    !!csr &&
    csr.section &&
    (
      csr.section.heading ||
      csr.blocks?.length > 0 ||
      csr.images?.length > 0
    );

  // For Mentorship
  const hasMentorship =
    !!mentorship &&
    mentorship.section &&
    mentorship.section.heading;

  // Scroll animations matching Schools.jsx
  const [aboutHeaderRef, isAboutHeaderVisible] = useScrollAnimation(0.2);
  const [aboutContentRef, isAboutContentVisible] = useScrollAnimation(0.2);

  const [affHeaderRef, isAffHeaderVisible] = useScrollAnimation(0.2);
  const [affContentRef, isAffContentVisible] = useScrollAnimation(0.2);

  const [awardsHeaderRef, isAwardsHeaderVisible] = useScrollAnimation(0.2);
  const [awardsContentRef, isAwardsContentVisible] = useScrollAnimation(0.2);

  const [csrHeaderRef, isCsrHeaderVisible] = useScrollAnimation(0.2);
  const [csrContentRef, isCsrContentVisible] = useScrollAnimation(0.2);

  const [mentorHeaderRef, isMentorHeaderVisible] = useScrollAnimation(0.2);
  const [mentorContentRef, isMentorContentVisible] = useScrollAnimation(0.2);

  return (
    <div>
      {/* Hero Image Section */}
      <SchoolInnerPageBanner />

      {/* About Content Section */}
      {hasAboutContent && (
        <div id="about-us" className="about-page-about-section">
          <div className="about-page-container">
            {/* <div ref={aboutHeaderRef} className={`section-header ${isAboutHeaderVisible ? "animate-in" : ""}`}>
              {about?.heading && (
                <h1 className="about-page-about-heading section-title-hm">
                  {about?.heading}
                </h1>
              )}
            </div> */}

            <div ref={aboutContentRef} className="row align-items-center">
              
              {/* TEXT */}
              <div className={`col-lg-8 schools-left ${isAboutContentVisible ? "animate-in" : ""}`}>
                <div ref={aboutHeaderRef} className={`section-header ${isAboutHeaderVisible ? "animate-in" : ""}`}>
              {about?.heading && (
                <h1 className="about-page-about-heading section-title-hm">
                  {about?.heading}
                </h1>
              )}
            </div>
                {about?.paragraph_1 && <p>{about.paragraph_1}</p>}
                {about?.paragraph_2 && <p>{about.paragraph_2}</p>}
                {about?.paragraph_3 && <p>{about.paragraph_3}</p>}
              </div>

              {/* IMAGE */}
              {about?.about_section_image && (
                <div className={`col-lg-4 schools-right ${isAboutContentVisible ? "animate-in" : ""}`}>
                  <img
                    src={`${about.about_section_image}`}
                    alt={about.heading || "About"}
                    className="about-page-about-image"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Affiliations and Certifications Section */}
      {hasAffiliation &&
        (
          affiliation.section.paragraph_1 ||
          affiliation.section.paragraph_2 ||
          affiliation.section.paragraph_3 ||
          affiliation.rows?.length > 0
        ) && (
          <div id="affiliations-and-certifications" className="about-page-affiliations-section">
            <div className="about-page-container">
              <div ref={affHeaderRef} className={`section-header ${isAffHeaderVisible ? "animate-in" : ""}`}>
                <h2 className="about-page-affiliations-title section-title-hm">
                  {affiliation.section.heading}
                </h2>
              </div>

              <div ref={affContentRef} className={`schools-right ${isAffContentVisible ? "animate-in" : ""}`}>
                {affiliation.section.paragraph_1 && (
                  <p className="about-page-affiliations-p">
                    {affiliation.section.paragraph_1}
                  </p>
                )}

                {affiliation.section.paragraph_2 && (
                  <p className="about-page-affiliations-p">
                    {affiliation.section.paragraph_2}
                  </p>
                )}

                {affiliation.section.paragraph_3 && (
                  <p className="about-page-affiliations-p-last">
                    {affiliation.section.paragraph_3}
                  </p>
                )}

                {/* TABLE */}
                {affiliation.rows && affiliation.rows.length > 0 && (
                  <div className="about-page-table-wrapper">
                    <table className="about-page-affiliations-table">
                      <thead>
                        <tr className="about-page-table-header-row">
                          <th className="about-page-table-header-cell">Sl. No.</th>
                          <th className="about-page-table-header-cell">Schools</th>
                          <th className="about-page-table-header-cell"> Classes</th>
                          <th className="about-page-table-header-cell">Affiliation No.</th>
                        </tr>
                      </thead>

                      <tbody>
                        {affiliation.rows.map((row, index) => (
                          <tr key={index}>
                            <td className="about-page-table-body-cell">{index + 1}</td>
                            <td className="about-page-table-body-cell">{row.school_name}</td>
                            <td className="about-page-table-body-cell">{row.class_range}</td>
                            <td className="about-page-table-body-cell-highlight">{row.affiliation_no}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {affiliation.section.paragraph_after_table && (
                  <p className="about-page-affiliations-p-after">
                    {affiliation.section.paragraph_after_table}
                  </p>
                )}
              </div>

            </div>
          </div>
        )}

      {/* Awards and Achievements Section */}
      {hasAwards &&
        (
          awards.section.intro_text ||
          awards.items?.length > 0 ||
          awards.section.highlight_text
        ) && (
          <div id="accolades-and-awards" className="about-page-awards-section">
            <div className="about-page-container">
              {/* Heading */}
              <div ref={awardsHeaderRef} className={`section-header ${isAwardsHeaderVisible ? "animate-in" : ""}`}>
                <h2 className="about-page-awards-title section-title-hm">
                  {awards.section.heading}
                </h2>
              </div>

              <div ref={awardsContentRef} className={`schools-right ${isAwardsContentVisible ? "animate-in" : ""}`}>
                {/* Intro text */}
                {awards.section.intro_text && (
                  <p className="about-page-awards-intro-p">
                    {awards.section.intro_text}
                  </p>
                )}

                {/* Award items */}
                {awards.items && awards.items.length > 0 && awards.items.map((item, idx) => (
                  <div key={idx} className="about-page-award-item-wrapper">
                    {item.title && (
                      <h4 className="about-page-award-item-title">
                        {item.title}
                      </h4>
                    )}

                    {item.description && (
                      <p className="about-page-award-item-desc">
                        {item.description}
                      </p>
                    )}
                  </div>
                ))}

                {/* Highlight text */}
                {awards.section.highlight_text && (
                  <p className="accolades-awards-p">
                    {awards.section.highlight_text}
                  </p>
                )}

                {/* Image – render ONLY if exists */}
                {awards.section.accolades_section_image && (
                  <div className="about-page-awards-image-wrapper">
                    <img
                      src={awards.section.accolades_section_image}
                      alt={awards.section.heading}
                      className="about-page-awards-image"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                )}
              </div>

            </div>
          </div>
        )}

      {/* Our Programs towards the Society Section */}
      {hasCsr && (
        <div id="our-programs-towards-the-society" className="about-page-csr-section">
          <div className="about-page-container">
            <div ref={csrHeaderRef} className={`section-header ${isCsrHeaderVisible ? "animate-in" : ""}`}>
              <h2 className="about-page-csr-title section-title-hm">{csr.section.heading}</h2>
            </div>

            <div ref={csrContentRef} className={`schools-right ${isCsrContentVisible ? "animate-in" : ""}`}>
              <h3 className="about-page-csr-subtitle">{csr.section.intro_heading}</h3>
              <p className="about-page-csr-intro-p">{csr.section.intro_paragraph}</p>

              {csr.blocks.map((item, idx) => (
                <div key={idx}>
                  <h4 className="about-page-csr-block-title">{item.block_heading}</h4>
                  <p className="about-page-csr-block-content">{item.block_content}</p>
                </div>
              ))}

              <p className="about-page-csr-last-p">{csr.section.last_paragraph}</p>

              <div className="row">
                {csr.images.map((img, i) => (
                  <div key={i} className="col-md-4 mb-3">
                    <img src={img.image_name} className="about-page-csr-image" />
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Mentorship Section */}
      {hasMentorship && (
        <div id="mentorship" className="about-page-mentorship-section">
          <div className="about-page-container">
            <div ref={mentorHeaderRef} className={`section-header ${isMentorHeaderVisible ? "animate-in" : ""}`}>
              <h2 className="about-page-mentorship-title section-title-hm">{mentorship.section.heading}</h2>
            </div>

            <div ref={mentorContentRef} className={`schools-right ${isMentorContentVisible ? "animate-in" : ""}`}>
              <p className="about-page-mentorship-quote-1">
                {mentorship.section.quote_1}
              </p>

              <p className="about-page-mentorship-p-1">{mentorship.section.paragraph_1}</p>
              <p className="about-page-mentorship-p-2">{mentorship.section.paragraph_2}</p>

              <p className="accolades-awards-p">
                {mentorship.section.highlight_text_1}
              </p>

              <div className="row about-page-mentorship-images-wrapper">
                {mentorship.images.map((c, i) => (
                  <div key={i} className="col-md-3 col-6 mb-4">
                    <img
                      src={c.image_name}
                      className="about-page-mentorship-image"
                    />
                    <h4 className="about-page-mentorship-caption">
                      {c.caption}
                    </h4>
                  </div>
                ))}
              </div>
              <p className="about-page-mentorship-highlight-text-2">
                {mentorship.section.highlight_text_2}
              </p>

              <p className="about-page-mentorship-quote-2">
                {mentorship.section.quote_2}
              </p>
            </div>

          </div>
        </div>
      )}
    </div>
  );



};
export default AboutPage;